import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';
import FileUploader from './FileUploader';
import ValidationSummaryModal from './ValidationSummaryModal';
import { translationService } from '../utils/api';

/**
 * Error message component with more detailed instructions
 */
const ValidationErrorMessage = ({ error }) => {
  const { t } = useTranslation();
  
  // Debug logging to see the actual error message
  console.log('Displaying validation error:', error);
  
  // Check if the error is a JSON string that wasn't properly parsed
  let parsedError = error;
  try {
    // If the error is a stringified JSON object, try to parse it
    if (error.startsWith('{') && error.endsWith('}')) {
      const errorObj = JSON.parse(error);
      if (errorObj.error) {
        parsedError = errorObj.error;
        console.log('Extracted error from JSON string:', parsedError);
      }
    }
  } catch (e) {
    // If parsing fails, just use the original error
    console.log('Error is not a valid JSON string, using as-is');
  }
  
  // Determine which specific error message to show based on the error text
  const getDetailedErrorMessage = () => {
    // Use the parsed error for all message matching
    const errorText = parsedError;
    
    if (errorText.includes('empty') || errorText.includes('invalid format')) {
      return t('translations.fileEmpty');
    }
    if (errorText.includes('Required columns') || errorText.includes('ID, Type, Reference Name')) {
      // Always display the exact error message from the server for column issues
      return errorText;
    }
    if (errorText.includes('No valid language columns')) {
      return t('translations.noLanguageColumns');
    }
    if (errorText.includes('duplicate translations') || errorText.includes('Duplicate translation')) {
      return t('translations.duplicateTranslations', { defaultValue: 'The file contains duplicate translations. Each translation must be unique within each language.' });
    }
    // For Bad Request errors, try to extract more specific information
    if (errorText.includes('Bad Request')) {
      return t('translations.badRequestHelp');
    }
    return errorText;
  };
  
  // Determine if we should show specific help instructions based on the error
  const shouldShowSpecificHelp = () => {
    const errorText = parsedError;
    return !errorText.includes('duplicate translations') && !errorText.includes('Duplicate translation');
  };
  
  // Determine if we should show specific duplicate translation help
  const shouldShowDuplicateHelp = () => {
    const errorText = parsedError;
    return errorText.includes('duplicate translations') || errorText.includes('Duplicate translation');
  };
  
  return (
    <div className="bg-red-50 border border-red-200 rounded-md p-4 mt-3">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">
            {t('translations.validationError')}
          </h3>
          <div className="mt-2 text-sm text-red-700">
            <p>{t('translations.validationErrorDetails')}</p>
            <p className="font-medium mt-1">{getDetailedErrorMessage()}</p>
            
            {shouldShowSpecificHelp() && (
              <div className="mt-3 p-3 bg-gray-50 rounded-md">
                <h4 className="font-medium mb-1 text-gray-700">{t('common.help')}:</h4>
                <ul className="list-disc pl-5 space-y-1 text-gray-600">
                  <li>{t('translations.wrongFileFormat')}</li>
                  <li>{t('translations.fileStructureExample')}</li>
                  <li>{t('translations.tryExportFirst')}</li>
                </ul>
              </div>
            )}
            
            {shouldShowDuplicateHelp() && (
              <div className="mt-3 p-3 bg-gray-50 rounded-md">
                <h4 className="font-medium mb-1 text-gray-700">{t('common.help')}:</h4>
                <ul className="list-disc pl-5 space-y-1 text-gray-600">
                  <li>{t('translations.duplicateHelp1', { defaultValue: 'Check for duplicate translations within the same language column.' })}</li>
                  <li>{t('translations.duplicateHelp2', { defaultValue: 'Ensure translations are unique across all dishes in the same language.' })}</li>
                  <li>{t('translations.duplicateHelp3', { defaultValue: 'Try exporting the file again to get the latest data.' })}</li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Component for bulk translation operations
 * 
 * @param {Object} props
 * @param {number} props.schoolId - The ID of the current school
 * @param {Array<{code: string, name: string}>} props.selectedLanguages - Array of selected languages for the school
 * @param {string} props.userLanguage - The current UI language code (e.g., 'en')
 * @param {Function} props.onImportComplete - Callback when import is complete
 */
const BulkTranslationSection = ({ schoolId, selectedLanguages, userLanguage, onImportComplete }) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Reset all states
  const resetStates = () => {
    setSelectedFile(null);
    setValidationResult(null);
    setValidationError(null);
    setIsImporting(false);
    setImportResult(null);
  };
  
  // Handle export button click - Use new endpoint for missing+selected
  const handleExport = async () => {
    if (!selectedLanguages || selectedLanguages.length === 0) {
      alert(t('translations.selectLanguagesFirst'));
      return;
    }
    
    try {
      setIsExporting(true);
      const codes = selectedLanguages.map(l => l.code);
      const result = await translationService.exportSelectedTranslations(schoolId, codes);
      saveAs(result.blob, result.filename);
      setIsExporting(false);
    } catch (error) {
      console.error('Error exporting selected translations:', error);
      setIsExporting(false);
      alert(t('translations.exportError', { error: error.message }));
    }
  };
  
  // Handle export all button click - Use new endpoint for all+selected
  const handleExportAll = async () => {
    if (!selectedLanguages || selectedLanguages.length === 0) {
      alert(t('translations.selectLanguagesFirst'));
      return;
    }
    
    try {
      setIsExporting(true);
      const codes = selectedLanguages.map(l => l.code);
      const result = await translationService.exportAllSelectedTranslations(schoolId, codes);
      saveAs(result.blob, result.filename);
      setIsExporting(false);
    } catch (error) {
      console.error('Error exporting all selected translations:', error);
      setIsExporting(false);
      alert(t('translations.exportError', { error: error.message }));
    }
  };
  
  // Handle file selection
  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setValidationResult(null);
    setValidationError(null);
    setImportResult(null);
  };
  
  // Handle file validation
  const handleValidate = async () => {
    if (!selectedFile) return;
    
    try {
      setIsValidating(true);
      setValidationError(null);
      
      const result = await translationService.validateTranslationFile(selectedFile, schoolId);
      
      setValidationResult(result);
      setIsModalOpen(true);
      setIsValidating(false);
    } catch (error) {
      console.error('Error validating translation file:', error);
      setValidationError(error.message);
      setIsValidating(false);
    }
  };
  
  // Handle import confirmation
  const handleImportConfirm = async () => {
    if (!validationResult || !validationResult.validationId) return;
    
    try {
      setIsImporting(true);
      
      // Derive selected codes string from the prop
      const selectedCodesString = selectedLanguages?.map(l => l.code).join(',') || '';
      
      // Pass the new parameters to the import service call
      const result = await translationService.importTranslations(
        validationResult.validationId,
        schoolId,
        selectedCodesString, 
        userLanguage 
      );
      
      setImportResult(result);
      setIsImporting(false);
      
      // Call the onImportComplete callback if provided
      if (onImportComplete) {
        onImportComplete(result);
      }
    } catch (error) {
      console.error('Error importing translations:', error);
      setIsImporting(false);
      alert(t('translations.importError', { error: error.message }));
    }
  };
  
  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    
    // If import was successful, reset all states
    if (importResult) {
      setTimeout(() => {
        resetStates();
      }, 500);
    }
  }, [importResult]);
  
  return (
    <div className="bg-white rounded-lg p-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">
        {t('translations.bulkTranslations')}
      </h2>
      
      <div className="space-y-6">
        {/* Export section */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            {t('translations.step1')}
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            {t('translations.exportDescription')}
          </p>
          <div className="flex space-x-3">
            <button
              type="button"
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isExporting ? 'opacity-75 cursor-wait' : ''
              }`}
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('translations.exporting')}
                </>
              ) : (
                <>
                  <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {t('translations.exportPendingTranslations', { defaultValue: 'Export pending translations' })}
                </>
              )}
            </button>
            <button
              type="button"
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isExporting ? 'opacity-75 cursor-wait' : ''
              }`}
              onClick={handleExportAll}
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('translations.exporting')}
                </>
              ) : (
                <>
                  <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  {t('translations.exportAllTranslations', { defaultValue: 'Export all translations' })}
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Upload section */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            {t('translations.step2')}
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            {t('translations.uploadDescription')}
          </p>
          <FileUploader
            onFileSelect={handleFileSelect}
            acceptedFileTypes=".xlsx"
            maxSizeMB={5}
            label={t('translations.selectFile')}
            disabled={isValidating || isImporting}
          />
          {validationError && (
            <ValidationErrorMessage error={validationError} />
          )}
        </div>
        
        {/* Validate section */}
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            {t('translations.step3')}
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            {t('translations.validateDescription')}
          </p>
          <button
            type="button"
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
              !selectedFile || isValidating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            onClick={handleValidate}
            disabled={!selectedFile || isValidating}
          >
            {isValidating ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('translations.validating')}
              </>
            ) : (
              <>
                <svg className="-ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                {t('translations.validateAndImport')}
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Validation/Import summary modal */}
      <ValidationSummaryModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onConfirm={handleImportConfirm}
        validationResult={validationResult}
        isImporting={isImporting}
        importResult={importResult}
      />
    </div>
  );
};

export default BulkTranslationSection; 